/**
 * The update logic behind the home screen's "Check for updates" / "Update now"
 * button, and behind the automatic update when a new worker is waiting.
 *
 * No DOM, no `virtual:` imports and no globals: everything the browser provides
 * comes in through `UpdateDeps`, so tests drive it with fakes.
 *
 * What an update may delete: the service worker registration and Cache Storage
 * (the app shell and anything cached by workers, e.g. a pending shared file).
 * It never touches IndexedDB (saved walks) or localStorage (settings): nothing
 * here receives a handle to either, and tests/updateFlow.test.ts fails if that
 * ever changes.
 */

export type Phase = "idle" | "checking" | "up-to-date" | "ready" | "applying" | "failed";

export interface UpdateStatus {
  phase: Phase;
  /** Newest version known to be deployed (from version.json), or null if unknown. */
  latest: string | null;
  /** Failure text for the "failed" phase. */
  message: string | null;
}

export const INITIAL_STATUS: UpdateStatus = { phase: "idle", latest: null, message: null };

export type UpdateEvent =
  | { type: "check-start" }
  | { type: "check-done"; latest: string | null; updateAvailable: boolean }
  | { type: "worker-waiting"; latest: string | null }
  | { type: "apply-start" }
  | { type: "fail"; message: string };

/** Pure transition table. A check or an apply already running is never restarted by a stray event. */
export function transition(status: UpdateStatus, event: UpdateEvent): UpdateStatus {
  switch (event.type) {
    case "check-start":
      if (status.phase === "checking" || status.phase === "applying") return status;
      return { phase: "checking", latest: status.latest, message: null };
    case "check-done":
      if (status.phase !== "checking") return status;
      return event.updateAvailable
        ? { phase: "ready", latest: event.latest, message: null }
        : { phase: "up-to-date", latest: event.latest, message: null };
    case "worker-waiting":
      if (status.phase === "applying") return status;
      return { phase: "ready", latest: event.latest ?? status.latest, message: null };
    case "apply-start":
      if (status.phase === "applying") return status;
      return { phase: "applying", latest: status.latest, message: null };
    case "fail":
      return { phase: "failed", latest: status.latest, message: event.message };
  }
}

export interface ButtonView {
  label: string;
  disabled: boolean;
  /** Extra line next to the button (failure text), or "". */
  note: string;
}

/** What the button shows in each phase. `current` is the running version without the "v". */
export function buttonView(status: UpdateStatus, current: string): ButtonView {
  switch (status.phase) {
    case "idle":
      return { label: "Check for updates", disabled: false, note: "" };
    case "checking":
      return { label: "Checking…", disabled: true, note: "" };
    case "up-to-date":
      return { label: `Up to date – v${current}`, disabled: false, note: "" };
    case "ready":
      return {
        label:
          status.latest && status.latest !== current
            ? `Update now — v${current} → v${status.latest}`
            : "Update now",
        disabled: false,
        note: "",
      };
    case "applying":
      return { label: "Updating…", disabled: true, note: "" };
    case "failed":
      return { label: "Retry", disabled: false, note: status.message ?? "Could not update." };
  }
}

/** Parses "1.2.3" (a leading "v" and a "-prerelease" / "+build" suffix are ignored). */
export function parseVersion(text: string): [number, number, number] | null {
  const m = /^v?(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/.exec(text.trim());
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

/** True when `candidate` is a strictly newer version than `current`. Unparseable versions are never newer. */
export function isNewerVersion(candidate: string, current: string): boolean {
  const a = parseVersion(candidate);
  const b = parseVersion(current);
  if (!a || !b) return false;
  for (let i = 0; i < 3; i++) if (a[i] !== b[i]) return (a[i] ?? 0) > (b[i] ?? 0);
  return false;
}

/** version.json: `{ "version": "1.2.3" }` (plus whatever the build adds). Anything else is "unknown". */
export function parseVersionFile(text: string): string | null {
  try {
    const data = JSON.parse(text) as { version?: unknown };
    return typeof data.version === "string" && parseVersion(data.version) ? data.version : null;
  } catch {
    return null; // e.g. a host that answers unknown URLs with index.html
  }
}

/** The last update attempt, kept in sessionStorage so a reload loop can be recognised. */
export interface AttemptRecord {
  /** The version that was running when the attempt started. */
  from: string;
  /** How many attempts in a row ended on that same version. */
  count: number;
  at: number;
}

export interface AttemptStore {
  read(): AttemptRecord | null;
  write(record: AttemptRecord): void;
  clear(): void;
}

export interface WorkerLike {
  state: string;
  postMessage(message: unknown): void;
  addEventListener(type: "statechange", listener: () => void): void;
  removeEventListener(type: "statechange", listener: () => void): void;
}

export interface RegistrationLike {
  waiting: WorkerLike | null;
  installing: WorkerLike | null;
  update(): Promise<unknown>;
  unregister(): Promise<boolean>;
}

export interface CacheStorageLike {
  keys(): Promise<string[]>;
  delete(name: string): Promise<boolean>;
}

export interface UpdateDeps {
  /** package.json version of the running build, without "v". */
  currentVersion: string;
  /** The page's service worker registration, or null (none / unsupported). */
  getRegistration(): Promise<RegistrationLike | null>;
  /** Deployed version from version.json fetched past every cache; null if unknown. Rejects when offline. */
  fetchLatestVersion(): Promise<string | null>;
  cacheStorage: CacheStorageLike | undefined;
  /** Resolves true on the next `controllerchange`, false after `timeoutMs`. Called before the worker is told to activate. */
  controllerChange(timeoutMs: number): Promise<boolean>;
  reload(): void;
  /** True when reloading now interrupts nothing: home screen showing, no walk open or opening. */
  isIdle(): boolean;
  attempts: AttemptStore;
  now(): number;
  /** Lets a test shrink the waits. */
  timeouts?: Partial<Timeouts>;
}

export interface Timeouts {
  /** registration.update() / version.json. */
  check: number;
  /** A new worker downloading its precache. */
  install: number;
  /** Activation after SKIP_WAITING. */
  activate: number;
  /** Unregister + clear caches. */
  force: number;
}

export const DEFAULT_TIMEOUTS: Timeouts = {
  check: 20_000,
  install: 120_000,
  activate: 10_000,
  force: 20_000,
};

/** Attempts closer together than this count as one loop. */
export const LOOP_WINDOW_MS = 2 * 60_000;
/** Attempts that ended on the same version before we stop trying. */
export const MAX_ATTEMPTS = 3;

export const MESSAGES = {
  offlineCheck: "Offline, cannot check for updates.",
  offlineApply: "Offline, cannot update right now.",
  busy: "Close the open walk first, then update.",
  downloading: "The update is still downloading. Try again in a moment.",
  notStarted: "The new version did not start. Tap Retry.",
  stuck:
    "Still on the old version after several tries. Close the app completely (swipe it away) and open it again.",
  failed: "Could not update. Tap Retry.",
};

class Timeout extends Error {}
/** The server cannot be reached. */
class OfflineError extends Error {}
/** A new worker did not finish downloading in time. */
class DownloadTimeout extends Error {}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Timeout("timeout")), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** Resolves once `worker` is installed (true) or gave up (false). */
function installed(worker: WorkerLike): Promise<boolean> {
  return new Promise((resolve) => {
    const done = (): boolean => {
      if (worker.state === "installed" || worker.state === "activating" || worker.state === "activated") {
        resolve(true);
        return true;
      }
      if (worker.state === "redundant") {
        resolve(false);
        return true;
      }
      return false;
    };
    if (done()) return;
    const listener = (): void => {
      if (done()) worker.removeEventListener("statechange", listener);
    };
    worker.addEventListener("statechange", listener);
  });
}

export class UpdateFlow {
  private current: UpdateStatus = INITIAL_STATUS;
  /** Set once an activation did not happen: the next apply goes straight to the forced path. */
  private escalate = false;
  private lastAction: "check" | "apply" = "check";
  /** True while apply() looks up the registration, before the "applying" phase starts. */
  private deciding = false;
  private readonly timeouts: Timeouts;

  private readonly deps: UpdateDeps;
  private readonly onChange: (status: UpdateStatus) => void;

  constructor(deps: UpdateDeps, onChange: (status: UpdateStatus) => void) {
    this.deps = deps;
    this.onChange = onChange;
    this.timeouts = { ...DEFAULT_TIMEOUTS, ...deps.timeouts };
  }

  get status(): UpdateStatus {
    return this.current;
  }

  private dispatch(event: UpdateEvent): void {
    const next = transition(this.current, event);
    if (next === this.current) return;
    this.current = next;
    this.onChange(next);
  }

  /** Call at page start: an earlier attempt that changed the version is done; clear its record. */
  settle(): void {
    const record = this.deps.attempts.read();
    if (!record) return;
    if (record.from !== this.deps.currentVersion || this.deps.now() - record.at > LOOP_WINDOW_MS) {
      this.deps.attempts.clear();
    }
  }

  /** A new worker finished installing and waits (workbox-window's `waiting`). */
  workerWaiting(): void {
    this.dispatch({ type: "worker-waiting", latest: null });
  }

  /** The button after a failure: repeat whatever failed. */
  retry(): Promise<void> {
    return this.lastAction === "apply" ? this.apply() : this.check();
  }

  /** Asks the server for a newer worker and a newer version.json, and says what it found. */
  async check(): Promise<void> {
    if (this.current.phase === "checking" || this.current.phase === "applying") return;
    this.lastAction = "check";
    this.dispatch({ type: "check-start" });
    const { deps, timeouts } = this;
    try {
      const registration = await deps.getRegistration().catch(() => null);
      const [updated, version] = await Promise.allSettled([
        registration ? withTimeout(registration.update(), timeouts.install) : Promise.resolve(),
        withTimeout(deps.fetchLatestVersion(), timeouts.check),
      ]);
      if (updated.status === "rejected" && version.status === "rejected") {
        this.dispatch({ type: "fail", message: MESSAGES.offlineCheck });
        return;
      }
      const latest = version.status === "fulfilled" ? version.value : null;
      if (registration?.installing) {
        // Found a newer worker: it is downloading the whole new app. Wait for it, then offer it.
        const ok = await withTimeout(installed(registration.installing), timeouts.install);
        if (!ok && !registration.waiting) {
          this.dispatch({ type: "fail", message: MESSAGES.failed });
          return;
        }
      }
      const newerDeployed = latest !== null && isNewerVersion(latest, deps.currentVersion);
      this.dispatch({
        type: "check-done",
        latest,
        updateAvailable: Boolean(registration?.waiting) || newerDeployed,
      });
    } catch (error) {
      this.dispatch({ type: "fail", message: error instanceof Timeout ? MESSAGES.downloading : MESSAGES.failed });
    }
  }

  /**
   * Switches to the new version and reloads the page.
   *
   * 1. A waiting worker: tell it to take over (SKIP_WAITING), reload on controllerchange.
   * 2. A worker still installing: wait for it, then as 1.
   * 3. Nothing to switch to (the browser never noticed the new worker, or a previous
   *    switch did not stick): unregister the worker, delete every cache, reload; the
   *    page then comes from the network and installs the current worker.
   *
   * `auto` is the unattended call (a worker started waiting): it never retries a
   * switch that already failed, so a worker that will not activate cannot loop.
   */
  async apply({ auto = false } = {}): Promise<void> {
    const { deps, timeouts } = this;
    if (this.current.phase === "applying" || this.deciding) return;
    if (!deps.isIdle()) {
      if (!auto) this.dispatch({ type: "fail", message: MESSAGES.busy });
      return;
    }
    this.deciding = true;
    const registration = await deps.getRegistration().catch(() => null);
    this.deciding = false;
    // Unattended, only ever switch to a worker that is already waiting; the forced path needs a click.
    if (auto && !registration?.waiting) return;
    const previous = deps.attempts.read();
    const recent = previous !== null && previous.from === deps.currentVersion && deps.now() - previous.at < LOOP_WINDOW_MS;
    if (auto && recent) return; // reloaded already and still the same version: leave it to the user
    const count = recent ? previous.count + 1 : 1;
    if (count > MAX_ATTEMPTS) {
      this.dispatch({ type: "fail", message: MESSAGES.stuck });
      return;
    }
    this.lastAction = "apply";
    this.dispatch({ type: "apply-start" });
    try {
      const forced = this.escalate || count >= 2;
      const worker = forced ? null : await this.activatableWorker(registration);
      if (worker) {
        // Listen first: the event can fire as soon as the message is handled.
        const changed = deps.controllerChange(timeouts.activate);
        worker.postMessage({ type: "SKIP_WAITING" });
        if (!(await changed)) {
          this.escalate = true;
          this.dispatch({ type: "fail", message: MESSAGES.notStarted });
          return;
        }
      } else {
        await withTimeout(this.force(registration), timeouts.force);
      }
      deps.attempts.write({ from: deps.currentVersion, count, at: deps.now() });
      deps.reload();
    } catch (error) {
      const message =
        error instanceof DownloadTimeout
          ? MESSAGES.downloading
          : error instanceof Timeout
            ? MESSAGES.notStarted
            : error instanceof OfflineError
              ? MESSAGES.offlineApply
              : MESSAGES.failed;
      if (error instanceof Timeout) this.escalate = true;
      this.dispatch({ type: "fail", message });
    }
  }

  /** The worker to send SKIP_WAITING to: the waiting one, or the installing one once it has installed. */
  private async activatableWorker(registration: RegistrationLike | null): Promise<WorkerLike | null> {
    if (!registration) return null;
    if (!registration.waiting && registration.installing) {
      await withTimeout(installed(registration.installing), this.timeouts.install).catch((error: unknown) => {
        throw error instanceof Timeout ? new DownloadTimeout() : error;
      });
    }
    return registration.waiting;
  }

  /** Drops the worker and every cache so the next load comes from the network. */
  private async force(registration: RegistrationLike | null): Promise<void> {
    // Never leave the device with no app and no network: confirm the server answers first.
    try {
      await this.deps.fetchLatestVersion();
    } catch {
      throw new OfflineError();
    }
    await registration?.unregister().catch(() => false);
    // Cache Storage only. IndexedDB (saved walks) and localStorage (settings) stay as they are.
    const caches = this.deps.cacheStorage;
    if (caches) await Promise.all((await caches.keys()).map((name) => caches.delete(name)));
  }
}
